import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

export type ColorMode = "light" | "dark";

const COLOR_MODE_STORAGE_KEY = "intern-color-mode";

function getSystemPreference(): ColorMode {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function getStoredPreference(): ColorMode | null {
  if (typeof window === "undefined") return null;
  const stored = localStorage.getItem(COLOR_MODE_STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return null;
}

interface ColorModeContextValue {
  colorMode: ColorMode;
  setColorMode: (mode: ColorMode | "system") => void;
  toggleColorMode: () => void;
  isSystemPreference: boolean;
}

const ColorModeContext = createContext<ColorModeContextValue | null>(null);

export function ColorModeProvider({ children }: { children: React.ReactNode }) {
  const [colorMode, setColorModeState] = useState<ColorMode>(() => {
    const stored = getStoredPreference();
    return stored ?? getSystemPreference();
  });

  const [isSystemPreference, setIsSystemPreference] = useState<boolean>(
    () => getStoredPreference() === null
  );

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (e: MediaQueryListEvent) => {
      if (isSystemPreference) {
        setColorModeState(e.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, [isSystemPreference]);

  // Apply color mode to document
  useEffect(() => {
    document.documentElement.setAttribute("data-color-mode", colorMode);
    document.documentElement.classList.remove("light", "dark");
    document.documentElement.classList.add(colorMode);
  }, [colorMode]);

  const setColorMode = useCallback((mode: ColorMode | "system") => {
    if (mode === "system") {
      localStorage.removeItem(COLOR_MODE_STORAGE_KEY);
      setIsSystemPreference(true);
      setColorModeState(getSystemPreference());
    } else {
      localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
      setIsSystemPreference(false);
      setColorModeState(mode);
    }
  }, []);

  const toggleColorMode = useCallback(() => {
    const newMode = colorMode === "light" ? "dark" : "light";
    setColorMode(newMode);
  }, [colorMode, setColorMode]);

  return (
    <ColorModeContext.Provider
      value={{
        colorMode,
        setColorMode,
        toggleColorMode,
        isSystemPreference,
      }}
    >
      {children}
    </ColorModeContext.Provider>
  );
}

export function useColorMode() {
  const context = useContext(ColorModeContext);
  if (!context) {
    throw new Error("useColorMode must be used within a ColorModeProvider");
  }
  return context;
}

export default ColorModeContext;
